import type { TemplateFormField } from '@common/types/templateFormFields';
import { AddOutlined } from '@mui/icons-material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoIcon from '@mui/icons-material/Info';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Box,
    Button,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    type SxProps,
    TextField,
    type Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import type { Operation } from 'fast-json-patch';
import { useEffect, useMemo, useRef, useState } from 'react';
import AlertDialog from '@/components/AlertDialog';
import AddButtonTextVariant from '@/components/Buttons/AddButtonTextVariant';
import CurrencyInput from '@/components/CurrencyInput';
import FullScreenLoader from '@/components/FullScreenLoader';
import { InfoSolidError } from '@/components/Icons/SystemIcons';
import { useOnMount } from '@/hooks/useOnMount';
import {
    deleteDisbursement,
    deleteRegistrationFees,
    deleteTitleInsuranceEstimates,
    editDisbursement,
    editRegistrationFees,
    updateTitleInsurance,
} from '@/services/PricingDetailsService';
import {
    addDynamicPrices,
    type DynamicFeesPayload,
    type DynamicPriceCategoryType,
    type DynamicPriceType,
    deleteDynamicPriceField,
    patchDynamicPriceField,
} from '@/services/QuoteTemplatesService';
import useQuoteIntakeSetupStore, {
    type EstimationDynamicValue,
} from '@/stores/useQuoteIntakeSetupStore';
import { formatCurrency, parseCurrency } from '@/utils';
import TitleInsuranceCalculator from './TitleInsuranceCalculator';

interface CostEstimationsProps {
    templateFormFields: TemplateFormField[];
}
export default function CostEstimations({ templateFormFields }: CostEstimationsProps) {
    const [
        activeQuote,
        formData,
        dynamicFeesErrors,
        setFormData,
        setCostEstimationsValues,
        fetchQuoteTemplateDataOnEveryStep,
        setDynamicPricing,
        setError,
        resetStep3Errors,
    ] = useQuoteIntakeSetupStore((state) => [
        state.activeQuote,
        state.formData,
        state.formData.step3.dynamicFeesErrors,
        state.setFormData,
        state.setCostEstimationsValues,
        state.fetchQuoteTemplateDataOnEveryStep,
        state.setDynamicPricing,
        state.setError,
        state.resetStep3Errors,
    ]);
    const theme = useTheme();
    const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
    const [deleteConfirmAlert, setDeleteConfirmAlert] = useState<{
        isOpen: boolean;
        index: number;
        dynamicFieldId: number | undefined;
    }>({
        isOpen: false,
        index: -1,
        dynamicFieldId: undefined,
    });
    const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
    const addOnFeeUpdateRef = useRef<number | undefined>(undefined);
    const [errors, setErrors] = useState<{
        missingValue: boolean | null;
        errorDetails: any;
    }>({
        missingValue: null,
        errorDetails: [],
    });
    const [isAddDisabled, setIsAddDisabled] = useState<boolean>(false);

    const quoteMainContainerCardStyles = {
        display: 'flex',
        gap: '24px',
        boxSizing: 'border-box',
        flexDirection: 'column',
        // (100vh - combined height, borders, paddings and margins of the header, footer and any margin, padding of the content area itself)
        maxHeight: 'calc(100vh - 168px)', // Todo: Move the heights of headers and footers to some sort of variables to better manage this
        minHeight: '400px',
        width: 'calc(100% - 64px)',
        padding: '32px',
        margin: '32px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        overflowY: 'auto',
    };

    const editableCurrencyInputStyles: SxProps<Theme> = {
        width: '100%',
        '& .MuiInputAdornment-positionEnd': {
            visibility: 'hidden',
            cursor: 'pointer',
            'svg path': {
                fill: theme.palette.sidenav.dividerBackground,
            },
        },
        '&:hover': {
            '& .MuiInputAdornment-positionEnd': {
                visibility: 'visible',
            },
        },
    };

    const editableTextFieldStyles: SxProps<Theme> = {
        width: '100%',
        '& .percent-adornment': {
            display: 'flex',
        },
        '& .edit-adornment': {
            display: 'none',
            cursor: 'pointer',
            'svg path': {
                fill: theme.palette.sidenav.dividerBackground,
            },
        },
        '&:hover': {
            '& .percent-adornment': {
                display: 'none',
            },
            '& .edit-adornment': {
                display: 'flex',
            },
        },
    };

    const constEstimationSectionTitle: SxProps<Theme> = {
        fontWeight: 500,
        lineHeight: '16px',
    };

    const estimationContainerStyles: SxProps<Theme> = {
        display: 'flex',
        gap: '24px',
        justifyContent: 'space-between',
    };

    const estimationSectionStyles: SxProps<Theme> = {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
    };

    const handleValueChange = (key: string, value: any): void => {
        setFormData('step3', {
            ...formData.step3,
            [key]: value,
        });
    };

    const handleAddClick = async (
        index: number,
        state: 'disbursementValues' | 'registrationFeeValues' | 'titleInsuranceEstimation',
    ) => {
        const updatedValues = formData.step3[state]?.map((item, i) => {
            if (i === index) {
                return { ...item, isAdded: true, isEditMode: false };
            } else {
                return item;
            }
        });

        //Todo: need to remove the if statement when integrating API
        if (state !== 'titleInsuranceEstimation') {
            setCostEstimationsValues(
                state === 'disbursementValues'
                    ? 'isDefaultDisbursementSet'
                    : 'isDefaultRegistrationSet',
                true,
            );
        }

        const selectedValue = updatedValues[index];
        if (selectedValue.id) {
            try {
                if (state === 'disbursementValues') {
                    await editDisbursement(selectedValue.id, selectedValue);
                }
                if (state === 'registrationFeeValues') {
                    await editRegistrationFees(selectedValue.id, selectedValue);
                }
                if (state === 'titleInsuranceEstimation') {
                    await updateTitleInsurance(selectedValue.id, {
                        description: selectedValue.description,
                        amount: selectedValue.amount,
                    });
                }
            } catch (err) {
                console.error(err);
            }
        }

        handleValueChange(state, updatedValues);
    };

    const handleEditClick = (
        index: number,
        state: 'disbursementValues' | 'registrationFeeValues' | 'titleInsuranceEstimation',
    ) => {
        const updatedValues = formData.step3[state]?.map((item, i) => {
            if (i === index) {
                return { ...item, isEditMode: true, isAdded: false };
            } else {
                return item;
            }
        });
        handleValueChange(state, updatedValues);
    };

    const handleRemoveClick = (
        index: number,
        state: 'disbursementValues' | 'registrationFeeValues' | 'titleInsuranceEstimation',
    ) => {
        const updatedValues = formData.step3[state]?.filter((item, i) => {
            if (i !== index) {
                return item;
            } else {
                if (item.id) {
                    if (state === 'disbursementValues') {
                        deleteDisbursement(item.id)
                            .then((_response) => {
                                const tempApiValues = formData.step3.disbursementApiValues.filter(
                                    (value) => value.feeEstimateId !== item.id,
                                );
                                setCostEstimationsValues('disbursementApiValues', tempApiValues);
                                setCostEstimationsValues('isDefaultDisbursementSet', true);
                            })
                            .catch((error) => console.error(error));
                    } else if (state === 'registrationFeeValues') {
                        deleteRegistrationFees(item.id)
                            .then((_response) => {
                                const tempApiValues =
                                    formData.step3.registrationFeeApiValues.filter(
                                        (value) => value.feeEstimateId !== item.id,
                                    );
                                setCostEstimationsValues('registrationFeeApiValues', tempApiValues);
                                setCostEstimationsValues('isDefaultRegistrationSet', true);
                            })
                            .catch((error) => console.error(error));
                    } else if (state === 'titleInsuranceEstimation') {
                        deleteTitleInsuranceEstimates(item.id).then((_response) => {
                            const tempApiValues = formData.step3.titleInsuranceApiValues.filter(
                                (value) => value.feeEstimateId !== item.id,
                            );
                            setCostEstimationsValues('titleInsuranceApiValues', tempApiValues);
                            setCostEstimationsValues('isDefaultTitleInsuranceSet', true);
                        });
                    }
                }
                //Todo: need to remove the if statement when integrating API
                if (state !== 'titleInsuranceEstimation') {
                    setCostEstimationsValues(
                        state === 'disbursementValues'
                            ? 'isDefaultDisbursementSet'
                            : 'isDefaultRegistrationSet',
                        true,
                    );
                }
                return;
            }
        });
        handleValueChange(state, updatedValues);
    };

    const handleDynamicValueChange = (
        index: number,
        value: any,
        field: 'description' | 'amount',
        state: 'disbursementValues' | 'registrationFeeValues' | 'titleInsuranceEstimation',
    ) => {
        const updatedValues = formData.step3[state]?.map((item, i) => {
            if (i === index) {
                const tempObj = { ...item };
                if (field === 'description') {
                    tempObj.description = value;
                } else {
                    if (state !== 'titleInsuranceEstimation') {
                        tempObj.amount = typeof value === 'string' ? parseCurrency(value) : value;
                    } else {
                        tempObj.amount = value;
                    }
                }
                return tempObj;
            } else {
                return item;
            }
        });
        handleValueChange(state, updatedValues);
    };

    const estimationDynamicForms = (
        descriptionLabel: string,
        values: EstimationDynamicValue[],
        onAddClick?: (index: number) => Promise<void>,
        onRemoveClick?: (index: number) => void,
        onEditClick?: (index: number) => void,
        onValueChange?: (index: number, value: any, field: 'description' | 'amount') => void,
        isTitleInsurance?: boolean,
    ) => {
        return (
            <>
                {values.map((value: EstimationDynamicValue, index: number) => (
                    <Box sx={{ marginTop: '8px', width: '100%' }} key={index}>
                        {value.isAdded && !value.isEditMode ? (
                            isTitleInsurance ? (
                                <TextField
                                    label={value.description}
                                    value={value.amount}
                                    sx={editableTextFieldStyles}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <>
                                                <InputAdornment
                                                    className="percent-adornment"
                                                    position="end"
                                                >
                                                    %
                                                </InputAdornment>
                                                <InputAdornment
                                                    className="edit-adornment"
                                                    position="end"
                                                >
                                                    <EditOutlinedIcon
                                                        onClick={
                                                            onEditClick &&
                                                            (() => onEditClick(index))
                                                        }
                                                        sx={{ cursor: 'pointer', fontSize: 16 }}
                                                    />
                                                </InputAdornment>
                                            </>
                                        ),
                                    }}
                                />
                            ) : (
                                <CurrencyInput
                                    allowDecimal={true}
                                    label={value.description}
                                    value={value.amount}
                                    sx={editableCurrencyInputStyles}
                                    inputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <EditOutlinedIcon
                                                    onClick={
                                                        onEditClick && (() => onEditClick(index))
                                                    }
                                                    sx={{ cursor: 'pointer', fontSize: 16 }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '16px',
                                }}
                            >
                                <TextField
                                    label={descriptionLabel}
                                    value={value.description}
                                    onChange={
                                        onValueChange &&
                                        ((
                                            e: React.ChangeEvent<
                                                HTMLInputElement | HTMLTextAreaElement
                                            >,
                                        ) => onValueChange(index, e.target.value, 'description'))
                                    }
                                />
                                {isTitleInsurance ? (
                                    <TextField
                                        label="Amount"
                                        value={value.amount}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">%</InputAdornment>
                                            ),
                                        }}
                                        onChange={
                                            onValueChange &&
                                            ((
                                                e: React.ChangeEvent<
                                                    HTMLInputElement | HTMLTextAreaElement
                                                >,
                                            ) => {
                                                const numericRegex = /^\d*(\.\d*)?$/;
                                                if (
                                                    numericRegex.test(e.target.value) ||
                                                    e.target.value === ''
                                                ) {
                                                    onValueChange(index, e.target.value, 'amount');
                                                }
                                            })
                                        }
                                    />
                                ) : (
                                    <CurrencyInput
                                        label="Amount"
                                        value={value.amount}
                                        allowDecimal={true}
                                        onChange={
                                            onValueChange &&
                                            ((value: any) => onValueChange(index, value, 'amount'))
                                        }
                                    />
                                )}
                                {!isTitleInsurance && (
                                    <IconButton
                                        aria-label="Remove"
                                        onClick={onRemoveClick && (() => onRemoveClick(index))}
                                    >
                                        <RemoveCircleOutlineIcon />
                                    </IconButton>
                                )}
                                {!value.isAdded && (
                                    <IconButton
                                        aria-label="Add"
                                        color="primary"
                                        disabled={value.description === '' || !value.amount}
                                        onClick={onAddClick && (() => onAddClick(index))}
                                    >
                                        <CheckRoundedIcon />
                                    </IconButton>
                                )}
                            </Box>
                        )}
                    </Box>
                ))}
            </>
        );
    };

    //Call  fetch api for step 3 part 2
    const fetchData = async () => {
        if (activeQuote.quoteTemplateId) {
            setIsFullScreenLoading(true);
            try {
                await fetchQuoteTemplateDataOnEveryStep('step3', activeQuote.quoteTemplateId, '2');
            } catch (error) {
                console.error(error);
            } finally {
                setIsFullScreenLoading(false);
            }
        }
    };

    const checkError = () => {
        const hiddenErrorDetails: any = [];
        const optionErrorDetails: any = [];
        const validQuestions = quoteOptionFields.map((option) => option.name);
        formData.step3.dynamicCostEstimationFields.map((field, index) => {
            if (field.question && !validQuestions.includes(field.question)) {
                hiddenErrorDetails.push(
                    {
                        field: 'question',
                        error: true,
                        index: index,
                    },
                    {
                        field: 'answer',
                        error: true,
                        index: index,
                    },
                );
            }

            const validOptions = getOptionsOfSelectedField(
                templateFormFields.find((f) => f.name === field.question),
            )?.map((option) => option.label);
            if (field.answer && !validOptions?.includes(field.answer)) {
                optionErrorDetails.push({
                    field: 'answer',
                    error: true,
                    index: index,
                });
            }
        });

        if (hiddenErrorDetails.length > 0) {
            setErrors({
                missingValue: true,
                errorDetails: hiddenErrorDetails,
            });
            setFormData('step3', {
                ...formData.step3,
                dynamicFeesErrors: {
                    ...dynamicFeesErrors,
                    costHiddenFieldError: true,
                },
            });
        }
        if (optionErrorDetails.length > 0) {
            setErrors({
                missingValue: true,
                errorDetails: optionErrorDetails,
            });
            setFormData('step3', {
                ...formData.step3,
                dynamicFeesErrors: {
                    ...dynamicFeesErrors,
                    optionError: true,
                },
            });
        }
    };

    useEffect(() => {
        fetchData();
    }, [activeQuote.quoteTemplateId]);

    useOnMount(() => {
        resetStep3Errors();
        checkError();
    });

    useEffect(() => {
        if (formData.step3.dynamicCostEstimationFields) {
            checkError();
        }
        let disableAdd = false;
        formData.step3.dynamicCostEstimationFields?.map((field) => {
            if (
                field.question === '' ||
                field.description === '' ||
                field.answer === '' ||
                field.amount === 0 ||
                !field.amount
            ) {
                disableAdd = true;
                return;
            }
        });
        setIsAddDisabled(disableAdd);
    }, [formData.step3.dynamicCostEstimationFields]);

    let estimationHelperInfo = '';
    if (formData.step0.transactionType === 'Purchase') {
        estimationHelperInfo = 'Estimated in % from purchase price';
    } else if (
        formData.step0.transactionType === 'Refinance' ||
        formData.step0.transactionType === 'PrivateLender'
    ) {
        estimationHelperInfo = 'Estimated in % from total loan amount';
    }

    //Methods for variable costs
    const quoteOptionFields = useMemo(() => {
        return formData.step1.templateFormFields.filter((field) => {
            if (
                field.isVisibleToUser &&
                (field.questionSchema.type === 'radio' || field.questionSchema.type === 'select')
            ) {
                return true;
            }

            //to include conditional questions
            if (field.questionSchema.isConditionalQuestion) {
                const conditionedByField = formData.step1.templateFormFields.find(
                    (f) => f.name === field.questionSchema.conditionedBy,
                );

                //If the field it is conditioned by is visible to the user, include this field
                if (
                    conditionedByField?.isVisibleToUser &&
                    (field.questionSchema.type === 'radio' ||
                        field.questionSchema.type === 'select')
                ) {
                    return true;
                }
            }

            return false;
        });
    }, [formData.step1.templateFormFields]);

    const getOptionsOfSelectedField = (field: TemplateFormField | undefined) => {
        if (!field) {
            return [] as { label: string; value: string; key: string }[];
        }

        if (field.questionSchema.type === 'radio') {
            return field.questionSchema.options?.map((op, index) => ({
                label: op.optionLabel,
                value: op.optionValue,
                key: `${field.name}-${index}`,
            }));
        }

        if (field.questionSchema.type === 'select') {
            return field.questionSchema.selectOptions?.map((sOp, index) => ({
                label: sOp.selectItemLabel,
                value: sOp.selectItemValue,
                key: `${field.name}-${index}`,
            }));
        }

        return [] as { label: string; value: string; key: string }[];
    };

    const updateVariableCost = async (
        payload: Operation[],
        quoteTemplateDynamicPriceId: number,
    ) => {
        try {
            await patchDynamicPriceField(quoteTemplateDynamicPriceId, payload);
        } catch (error: any) {
            setError(error.response?.data || 'Failed to update the field');

            const timeout = window.setTimeout(() => {
                setError(null);
                window.clearTimeout(timeout);
            }, 5000);
        }
    };

    const handleVariableCostValueChange = async (
        value: any,
        field: keyof DynamicPriceType,
        index: number,
        costCategory: DynamicPriceCategoryType,
        quoteTemplateDynamicPriceId?: number,
    ) => {
        if (quoteTemplateDynamicPriceId) {
            const payload: Operation[] = [
                {
                    op: 'replace',
                    path: `/${field}`,
                    value: value,
                },
            ];

            window.clearTimeout(addOnFeeUpdateRef.current);

            addOnFeeUpdateRef.current = window.setTimeout(() => {
                updateVariableCost(payload, quoteTemplateDynamicPriceId);
            }, 200);
        } else {
            if (field === 'dynamicPriceCategory') {
                // Call post API to create a new dynamic price
                const tempDynamicPricePayload: DynamicFeesPayload = {
                    dynamicPriceCategory: value,
                    question: '',
                    answer: '',
                    description: '',
                    amount: 0,
                };

                try {
                    if (activeQuote.quoteTemplateId) {
                        const dynamicFieldPostResp = await addDynamicPrices(
                            activeQuote.quoteTemplateId,
                            [tempDynamicPricePayload],
                        );

                        const newField = dynamicFieldPostResp.data[0];
                        newField[field] = value;
                        //Update the form data with the new field
                        const updatedFields = [...formData.step3.dynamicCostEstimationFields];
                        updatedFields[index] = newField;

                        handleValueChange('dynamicCostEstimationFields', updatedFields);
                    }
                } catch (error: any) {
                    console.error(error);
                    setError(error.response?.data || 'Failed to add the field');
                }
            } else {
                setError('Please select cost category before updating other fields');
            }
            const timeout = window.setTimeout(() => {
                setError(null);
                window.clearTimeout(timeout);
            }, 5000);
        }

        setDynamicPricing(costCategory, field, index, value);
    };

    const handleAddNewVariableCost = async () => {
        const newDynamicPrice: DynamicFeesPayload = {
            dynamicPriceCategory: '', // Default category for API call
            question: '',
            answer: '',
            description: '',
            amount: 0,
        };

        handleValueChange('dynamicCostEstimationFields', [
            ...formData.step3.dynamicCostEstimationFields,
            newDynamicPrice,
        ]);
    };

    const removeDynamicVariableCost = async (index: number, dynamicFieldId?: number) => {
        if (isConfirmDisabled) return;

        setIsConfirmDisabled(true);
        try {
            if (dynamicFieldId) {
                await deleteDynamicPriceField(dynamicFieldId);
            }
            const filteredDynamicFields = formData.step3.dynamicCostEstimationFields.filter(
                (f, i) => {
                    if (dynamicFieldId) {
                        return f.quoteTemplateDynamicPriceId !== dynamicFieldId;
                    }

                    return i !== index;
                },
            );

            // Filter errors to remove any related to the deleted field
            const filteredErrors = errors.errorDetails.filter((error) => error.index !== index);

            //Set the state with filtered fields
            setFormData('step3', {
                ...formData.step3,
                dynamicCostEstimationFields: filteredDynamicFields,
                dynamicFeesErrors: {
                    missingValue: null,
                    errorDetails: [],
                },
            });
            setErrors({
                missingValue: filteredErrors.length > 0,
                errorDetails: filteredErrors,
            });
            setDeleteConfirmAlert({
                isOpen: false,
                index: -1,
                dynamicFieldId: undefined,
            });
        } catch (error: any) {
            console.error(error);
            setError(error.response?.data || 'Failed to delete the field');

            const timeout = window.setTimeout(() => {
                setError(null);
                window.clearTimeout(timeout);
            }, 5000);
        }
        setIsConfirmDisabled(false);
    };

    const hasError = (field, index, errorList) =>
        errorList.some((err) => err?.field === field && err?.index === index && err?.error);

    return (
        <Box sx={quoteMainContainerCardStyles}>
            {isFullScreenLoading && <FullScreenLoader />}
            <div>
                <Typography sx={{ marginBottom: '8px' }} variant="h1" component="h1">
                    Cost Estimations
                </Typography>
                <Typography variant="body1" component="p">
                    Please provide details about your estimation process.
                </Typography>
            </div>
            <Box>
                <Typography variant="h3">Static costs</Typography>
                <Typography variant="body2" component="p">
                    Static costs are always the part of your quote. You can add custom costs to meet
                    your needs.
                </Typography>
            </Box>
            <Box sx={estimationContainerStyles}>
                <Box sx={estimationSectionStyles}>
                    <Typography variant="body2" sx={constEstimationSectionTitle}>
                        Disbursements estimation
                    </Typography>
                    {estimationDynamicForms(
                        'Disbursement description',
                        formData.step3.disbursementValues,
                        (index: number) => handleAddClick(index, 'disbursementValues'),
                        (index: number) => handleRemoveClick(index, 'disbursementValues'),
                        (index: number) => handleEditClick(index, 'disbursementValues'),
                        (index: number, value: any, field: 'description' | 'amount') =>
                            handleDynamicValueChange(index, value, field, 'disbursementValues'),
                        false,
                    )}
                    <AddButtonTextVariant
                        label="Add disbursement"
                        onClick={() =>
                            handleValueChange('disbursementValues', [
                                ...formData.step3.disbursementValues,
                                { amount: 0, description: '', isAdded: false },
                            ])
                        }
                    ></AddButtonTextVariant>
                    <Divider></Divider>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h4" sx={{ color: theme.palette.neutral.gray }}>
                            Total disbursements without variable costs
                        </Typography>
                        <Typography variant="h3">
                            ${' '}
                            {formatCurrency(
                                formData.step3.disbursementValues
                                    ?.reduce((acc, currValue) => {
                                        return acc + Number(currValue.amount);
                                    }, 0)
                                    ?.toFixed(2),
                            )}
                        </Typography>
                    </Box>
                </Box>
                <Divider orientation="vertical" />
                <Box sx={estimationSectionStyles}>
                    <Typography variant="body2" sx={constEstimationSectionTitle}>
                        Registration fees estimation
                    </Typography>
                    {estimationDynamicForms(
                        'Registration fee description',
                        formData.step3.registrationFeeValues,
                        (index: number) => handleAddClick(index, 'registrationFeeValues'),
                        (index: number) => handleRemoveClick(index, 'registrationFeeValues'),
                        (index: number) => handleEditClick(index, 'registrationFeeValues'),
                        (index: number, value: any, field: 'description' | 'amount') =>
                            handleDynamicValueChange(index, value, field, 'registrationFeeValues'),
                        false,
                    )}
                    <AddButtonTextVariant
                        onClick={() =>
                            handleValueChange('registrationFeeValues', [
                                ...formData.step3.registrationFeeValues,
                                { amount: 0, description: '', isAdded: false },
                            ])
                        }
                        label={'Add registration fee'}
                    ></AddButtonTextVariant>
                    <Divider></Divider>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h4" sx={{ color: theme.palette.neutral.gray }}>
                            Total registration fees without variable costs
                        </Typography>
                        <Typography variant="h3">
                            ${' '}
                            {formatCurrency(
                                formData.step3.registrationFeeValues
                                    ?.reduce((acc, currValue) => {
                                        return acc + Number(currValue.amount);
                                    }, 0)
                                    ?.toFixed(2),
                            )}
                        </Typography>
                    </Box>
                </Box>
                {(formData.step0.transactionType === 'Purchase' ||
                    formData.step0.transactionType === 'Refinance' ||
                    formData.step0.transactionType === 'PrivateLender') && (
                    <>
                        <Divider orientation="vertical" />
                        <Box sx={estimationSectionStyles}>
                            <Box sx={{ display: 'flex', gap: '10px' }}>
                                <Typography variant="body2" sx={constEstimationSectionTitle}>
                                    Title insurance estimation
                                </Typography>
                                <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                        <Typography variant="body2">
                                            {estimationHelperInfo}
                                        </Typography>
                                    }
                                >
                                    <IconButton sx={{ padding: '0px' }}>
                                        <InfoIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {estimationDynamicForms(
                                'Title estimation description',
                                formData.step3.titleInsuranceEstimation,
                                (index: number) =>
                                    handleAddClick(index, 'titleInsuranceEstimation'),
                                (index: number) =>
                                    handleRemoveClick(index, 'titleInsuranceEstimation'),
                                (index: number) =>
                                    handleEditClick(index, 'titleInsuranceEstimation'),
                                (index: number, value: any, field: 'description' | 'amount') => {
                                    handleDynamicValueChange(
                                        index,
                                        value,
                                        field,
                                        'titleInsuranceEstimation',
                                    );
                                },
                                true,
                            )}
                            <TitleInsuranceCalculator />
                        </Box>
                    </>
                )}
            </Box>
            {/* --------------------- Variable Costs ---------------------*/}
            <Divider />
            <Box>
                <Typography variant="h3">Variable costs</Typography>
                <Typography variant="body2">
                    Variable costs will be applied only if certain options will be selected by the
                    client during the completion of the quote form. <br />
                    Select the question, choose an option you want to set an additional charge for.
                </Typography>
            </Box>
            {(dynamicFeesErrors.missingValue ||
                dynamicFeesErrors.duplicateValue ||
                dynamicFeesErrors.optionError ||
                dynamicFeesErrors.costHiddenFieldError) && (
                <Alert icon={<InfoSolidError />} severity="error">
                    <AlertTitle>Error(s)</AlertTitle>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '25px' }}>
                        {dynamicFeesErrors.missingValue && (
                            <li>Please ensure no values are missing</li>
                        )}
                        {dynamicFeesErrors.duplicateValue && (
                            <li>
                                Please ensure there are no duplicate rows for questions and their
                                options.
                            </li>
                        )}
                        {dynamicFeesErrors.optionError && (
                            <li>
                                The selected option for this question has been edited or removed
                                from the quote form. Please select an option or remove the row to
                                proceed.
                            </li>
                        )}
                        {dynamicFeesErrors.costHiddenFieldError && (
                            <li>
                                This field has been removed or hidden from the quote form. If
                                hidden, please unhide it in the quote form or delete it from the
                                Variable Cost section to proceed.
                            </li>
                        )}
                    </ul>
                </Alert>
            )}
            {formData.step3.dynamicCostEstimationFields.map((field, index) => (
                <Box
                    key={field.quoteTemplateDynamicPriceId}
                    sx={{
                        display: 'flex',
                        gap: '16px',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <FormControl fullWidth>
                        <InputLabel id={`cost-category-label-${field.quoteTemplateDynamicPriceId}`}>
                            Cost Category
                        </InputLabel>
                        <Select
                            labelId={`cost-category-label-${field.quoteTemplateDynamicPriceId}`}
                            label="Cost Category"
                            value={field.dynamicPriceCategory}
                            onChange={(event) => {
                                handleVariableCostValueChange(
                                    event.target.value,
                                    'dynamicPriceCategory',
                                    index,
                                    field.dynamicPriceCategory,
                                    field.quoteTemplateDynamicPriceId,
                                );
                                resetStep3Errors();
                            }}
                            IconComponent={() => (
                                <InputAdornment position="end" sx={{ margin: '8px' }}>
                                    {dynamicFeesErrors.errorDetails.some(
                                        (err) =>
                                            err?.field === 'dynamicPriceCategory' &&
                                            err?.index === index &&
                                            err?.error,
                                    ) ? (
                                        <InfoSolidError />
                                    ) : (
                                        <ArrowDropDownIcon />
                                    )}
                                </InputAdornment>
                            )}
                            error={dynamicFeesErrors.errorDetails.some(
                                (err) =>
                                    err?.field === 'dynamicPriceCategory' &&
                                    err?.index === index &&
                                    err?.error,
                            )}
                        >
                            <MenuItem value={'DisbursementFee'}>Disbursement Fee</MenuItem>
                            <MenuItem value={'RegistrationFee'}>Registration Fee</MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        disablePortal
                        value={
                            field.dynamicPriceCategory
                                ? templateFormFields
                                      .map((f) => ({
                                          label: f.title,
                                          value: f.name,
                                          key: f.name,
                                      }))
                                      .find((f) => f.value === field.question) || null
                                : null
                        }
                        options={quoteOptionFields.map((f) => ({
                            label: f.questionSchema.label,
                            value: f.name,
                            key: f.name,
                        }))}
                        fullWidth
                        onChange={(_ev, value) => {
                            handleVariableCostValueChange(
                                value?.value,
                                'question',
                                index,
                                field.dynamicPriceCategory,
                                field.quoteTemplateDynamicPriceId,
                            );
                            resetStep3Errors();
                            setErrors({
                                missingValue: null,
                                errorDetails: [],
                            });
                        }}
                        popupIcon={
                            hasError('question', index, dynamicFeesErrors.errorDetails) ||
                            hasError('question', index, errors.errorDetails) ? (
                                <InfoSolidError />
                            ) : (
                                <ArrowDropDownIcon />
                            )
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Question"
                                error={
                                    hasError('question', index, dynamicFeesErrors.errorDetails) ||
                                    hasError('question', index, errors.errorDetails)
                                }
                            />
                        )}
                    />
                    <FormControl fullWidth>
                        <InputLabel
                            id={`dynamic-fee-option-label-${field.quoteTemplateDynamicPriceId}`}
                        >
                            Option
                        </InputLabel>
                        <Select
                            labelId={`dynamic-fee-option-label-${field.quoteTemplateDynamicPriceId}`}
                            label="Option"
                            value={field.answer}
                            onChange={(event) => {
                                handleVariableCostValueChange(
                                    event.target.value,
                                    'answer',
                                    index,
                                    field.dynamicPriceCategory,
                                    field.quoteTemplateDynamicPriceId,
                                );
                                resetStep3Errors();
                                setErrors({
                                    missingValue: null,
                                    errorDetails: [],
                                });
                            }}
                            IconComponent={() => (
                                <InputAdornment position="end" sx={{ margin: '8px' }}>
                                    {hasError('answer', index, dynamicFeesErrors.errorDetails) ||
                                    hasError('answer', index, errors.errorDetails) ? (
                                        <InfoSolidError />
                                    ) : (
                                        <ArrowDropDownIcon />
                                    )}
                                </InputAdornment>
                            )}
                            error={
                                hasError('answer', index, dynamicFeesErrors.errorDetails) ||
                                hasError('answer', index, errors.errorDetails)
                            }
                        >
                            {getOptionsOfSelectedField(
                                templateFormFields.find((f) => f.name === field.question),
                            )?.map((option) => {
                                return (
                                    <MenuItem value={option.value} key={option.key}>
                                        {option.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Divider orientation="vertical" />
                    <TextField
                        fullWidth
                        value={field.description}
                        label={'Fee description'}
                        onChange={(event) => {
                            handleVariableCostValueChange(
                                event.target.value,
                                'description',
                                index,
                                field.dynamicPriceCategory,
                                field.quoteTemplateDynamicPriceId,
                            );
                            resetStep3Errors();
                            setErrors({
                                missingValue: null,
                                errorDetails: [],
                            });
                        }}
                        error={hasError('description', index, dynamicFeesErrors.errorDetails)}
                        InputProps={{
                            endAdornment: hasError(
                                'description',
                                index,
                                dynamicFeesErrors.errorDetails,
                            ) ? (
                                <InputAdornment position="end">
                                    <InfoSolidError />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <CurrencyInput
                        sx={{ width: '50%' }}
                        value={parseCurrency(`${field.amount}`)}
                        onChange={(value) => {
                            handleVariableCostValueChange(
                                value,
                                'amount',
                                index,
                                field.dynamicPriceCategory,
                                field.quoteTemplateDynamicPriceId,
                            );
                            resetStep3Errors();
                            setErrors({
                                missingValue: null,
                                errorDetails: [],
                            });
                        }}
                        allowDecimal
                        label="Amount"
                        error={hasError('amount', index, dynamicFeesErrors.errorDetails)}
                        showErrorAdornment={true}
                    />
                    <IconButton
                        aria-label="remove-dynamic-field"
                        onClick={() =>
                            setDeleteConfirmAlert({
                                isOpen: true,
                                index: index,
                                dynamicFieldId: field.quoteTemplateDynamicPriceId,
                            })
                        }
                    >
                        <RemoveCircleOutlineIcon />
                    </IconButton>
                </Box>
            ))}
            <Button
                startIcon={<AddOutlined />}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                onClick={handleAddNewVariableCost}
                disabled={isAddDisabled}
            >
                Add fee
            </Button>
            <AlertDialog
                open={deleteConfirmAlert.isOpen}
                title="Are you sure ?"
                message={`Clicking on 'Accept' will delete the selected variable cost field permanently`}
                handleClose={() =>
                    setDeleteConfirmAlert({
                        isOpen: false,
                        index: -1,
                        dynamicFieldId: undefined,
                    })
                }
                handleConfirm={() => {
                    removeDynamicVariableCost(
                        deleteConfirmAlert.index,
                        deleteConfirmAlert.dynamicFieldId,
                    );
                }}
            />
        </Box>
    );
}
